import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, DataTable, FormField, Grid, Heading, List, Select, Tab, Tabs, Text, TextInput } from "grommet";
import { Dictionary, flatten, get, groupBy, keyBy, last, mapValues, orderBy, uniqBy, values, zipObject } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import { NavigationButton } from "../components/NavigationButton";
import RichTabTitle from "../components/RichTabTitle";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import ThemeBuilder from "../components/ThemeBuilder";
import { useReferenceData } from "../reference/ReferenceStore";
import { StatIcon } from "../stats/StatIcon";
import { RenderStat, StatValue } from "../stats/StatRenderer";
import { StatViewModel } from "../stats/StatsStore";
import { TeamName, TeamNameLogoWrapper } from "../teams/TeamName";
import { TeamRow } from "../teams/TeamsManagement";
import { getTeams, TeamViewModel } from "../teams/TeamsStore";

import SeedMatchDetails from "./SeedMatchDetails";
import { AddSeedMatchParticipantRequest, addParticipant, getSeedMatch, SeedMatchParticipantViewModel, SeedMatchViewModel, RemoveSeedMatchParticipantRequest, removeParticipant, updateSeedMatch, SeedMatchStatViewModel, ParticipantStatSummary, resetStats } from "./SeedMatchesStore";

import { SeedMatchParticipantStats } from "./SeedMatchParticipantStats";
import SeedMatchStatTable from "./SeedMatchStatTable";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;
const switchArrows = <FontAwesomeIcon icon={["fad", "arrows-left-right"]}></FontAwesomeIcon>;

const chain = <FontAwesomeIcon icon={["fad", "chain"]}></FontAwesomeIcon>;

const SeedMatchProfile = () => {
    let { id } = useParams<{ id: string }>();

    let { pathname } = useLocation();
    let navigate = useNavigate();

    const [seedMatch, setSeedMatch] = React.useState(new SeedMatchViewModel());
    const [isLoading, setIsLoading] = React.useState(true);
    const [participantRoundStats, setParticipantRoundStats] = React.useState([] as ParticipantStatSummary[]);
    const [rounds, setRounds] = React.useState([] as string[]);
    React.useEffect(() => {
        if (seedMatch.id) {
            if (pathname == `/seedMatches/${id}`) {
                if (seedMatch.participants.length > 0) {
                    navigate(`/seedMatches/${id}/teams`, { replace: true });
                } else {
                    navigate(`/seedMatches/${id}/details`, { replace: true });
                }
            }
        }
    }, [pathname, seedMatch.id]);

    let [error, setError] = React.useState("");

    React.useEffect(() => {
        loadSeedMatch();
    }, []);

    const loadSeedMatch = () => {
        getSeedMatch(id).then((seedMatch) => {
            setSeedMatch(seedMatch);
            setParticipants(seedMatch.participants);
            setIsLoading(false);
            setIsUpdating(false);

            let seedMatchStatsToSeedMatchSummary = (stats: Dictionary<SeedMatchStatViewModel[]>) => {
                return mapValues(stats, (s) => s.reduce((a, b) => ({ ...b, value: a.value + b.value }), { value: 0 } as SeedMatchStatViewModel));
            };

            let stats = seedMatch.participants.map(
                (p) =>
                    ({
                        id: p.id,
                        team: p.team,
                        stats: groupBy(p.stats, (s) => "Round " + s.round),
                        Total: seedMatchStatsToSeedMatchSummary(groupBy(p.stats, (p) => p.statID)),
                    } as ParticipantStatSummary)
            );

            let presentStatTypes = uniqBy(flatten(seedMatch.participants.map((p) => p.stats.map((s) => s.stat))), (s) => s.id);

            setParticipantRoundStats(
                orderBy(
                    stats,
                    presentStatTypes.map((s) => (p) => {
                        if (get(p, `Total.${s.id}.value`, 0) != 0) {
                            return get(p, `Total.${s.id}.value`);
                        } else {
                            return s.invertedScoring ? Number.MAX_VALUE : Number.MIN_VALUE;
                        }
                    }),
                    presentStatTypes.map((s) => (s.invertedScoring ? "asc" : "desc"))
                )
            );

            let _rounds = new Array(seedMatch.rounds).fill("Round ").map((x, i) => x + (i + 1));
            setRounds(_rounds);
        });
    };

    const [isUpdating, setIsUpdating] = React.useState(false);

    const onChange = (updatedSeedMatch) => {
        setSeedMatch({ ...seedMatch, ...updatedSeedMatch });
    };

    let update = () => {
        setIsUpdating(true);
        updateSeedMatch(seedMatch).then(
            (result) => {
                getSeedMatch(id).then((seedMatch) => setSeedMatch(seedMatch));
                setIsUpdating(false);
            },
            (error) => {
                setError(error);
                setIsUpdating(false);
            }
        );
    };

    const [teams, setTeams] = React.useState([] as TeamViewModel[]);

    React.useEffect(() => {
        getTeams("").then((teams) => {
            setTeams(teams);
            setTeamSearchResult(teams);
        });
    }, []);

    let [participants, setParticipants] = React.useState([] as SeedMatchParticipantViewModel[]);

    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);

    const search = (filter) => {
        setTeamSearchResult(
            teams.filter((r) => {
                return r.name.toLowerCase().includes(filter.toLowerCase());
            })
        );
    };

    let [teamFilter, setTeamFilter] = React.useState("");
    const [role, setRole] = React.useState("");

    React.useEffect(() => {
        search(teamFilter);
    }, [teamFilter]);

    const [showAddParticipantModal, setShowAddParticipantModal] = React.useState(false);
    const [showRemoveParticipantModal, setShowRemoveParticipantModal] = React.useState(false);

    const [participantToRemove, setParticipantToRemove] = React.useState({} as TeamViewModel);

    const [participantCreateError, setParticipantCreateError] = React.useState("");
    const [participantRemoveError, setParticipantRemoveError] = React.useState("");

    const addParticipantToSeedMatch = (team: TeamViewModel) => {
        setParticipantCreateError("");
        setIsLoading(true);
        addParticipant({ seedMatchID: seedMatch.id, teamID: team.id } as AddSeedMatchParticipantRequest).then((response) => {
            if (response.success) {
                loadSeedMatch();
            } else {
                setParticipantCreateError(response.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };

    const startRemoveParticipant = (participant: TeamViewModel) => {
        setParticipantToRemove(participant);
        setShowRemoveParticipantModal(true);
    };

    const removeParticipantFromSeedMatch = () => {
        setParticipantRemoveError("");
        setIsLoading(true);
        removeParticipant({ seedMatchID: seedMatch.id, teamID: participantToRemove.id } as RemoveSeedMatchParticipantRequest).then((response) => {
            if (response.success) {
                loadSeedMatch();
                setShowRemoveParticipantModal(false);
            } else {
                setParticipantRemoveError(response.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };

    const hasWritePermission = usePermission("sport:seedMatches.write");

    const [showArchiveModal, setShowArchiveModal] = React.useState(false);

    const startArchive = () => {
        setShowArchiveModal(true);
    };

    const archive = () => {
        updateSeedMatch({ ...seedMatch, archived: !seedMatch.archived } as SeedMatchViewModel).then(() => {
            loadSeedMatch();
            setShowArchiveModal(false);
        });
    };

    const [showRemoveAllStatsModal, setShowRemoveAllStatsModal] = React.useState(false);

    const startRemoveAllStats = () => {
        setShowRemoveAllStatsModal(true);
    };

    const removeAllStats = () => {
        resetStats({ seedMatchID: seedMatch.id }).then(() => {
            loadSeedMatch();
            setShowRemoveAllStatsModal(false);
        });
    };

    const [darkMode, setDarkMode] = React.useState(true);
    const [useTeamColor, setUseTeamColor] = React.useState(true);
    const [reverse, setReverse] = React.useState(false);
    const [backgroundColor, setBackgroundColor] = React.useState("transparent");

    const copyLink = (link) => () => {
        // @ts-ignore
        navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
            if (result.state == "granted" || result.state == "prompt") {
                navigator.clipboard.writeText(window.location.origin + link + `?darkMode=${darkMode}&useTeamColor=${useTeamColor}&reverse=${reverse}&background=${backgroundColor}`).then(
                    function () {
                        /* clipboard successfully set */
                    },
                    function () {
                        /* clipboard write failed */
                    }
                );
            }
        });
    };

    return (
        <div>
            <SkeletonWrapper loading={isLoading}>
                <Heading size="small">
                    {seedMatch.name} <br /> {seedMatch.archived && <Text size="small">Archived</Text>}
                </Heading>

                <Box direction="row" align="center" justify="center" gap="medium">
                    <NavigationButton href={`/seedMatches/${seedMatch.id}/table`} text={"Table"}></NavigationButton>
                    <NavigationButton href={`/seedMatches/${seedMatch.id}/stats`} text={"Stats"}></NavigationButton>
                    <NavigationButton href={`/seedMatches/${seedMatch.id}/teams`} text={"Teams"}></NavigationButton>
                    <NavigationButton href={`/seedMatches/${seedMatch.id}/details`} text={"Details"}></NavigationButton>
                    <NavigationButton href={`/seedMatches/${seedMatch.id}/public`} text={"Public"}></NavigationButton>
                    {error && <NavigationButton href={`/seedMatches/${seedMatch.id}/error`} text={"Errors"}></NavigationButton>}
                </Box>

                <Routes>
                    <Route
                        path="table"
                        element={
                            <>
                                <SeedMatchStatTable stats={participantRoundStats} rounds={rounds}></SeedMatchStatTable>
                            </>
                        }
                    />
                    <Route
                        path={`stats`}
                        element={
                            <>
                                <Box pad={"small"} gap="small">
                                    <Grid columns={["1fr", "5fr", ...rounds.map((r) => "2fr"), "2fr", "1fr"]} align="center" gap="medium">
                                        <Box>
                                            <Text>#</Text>
                                        </Box>
                                        <Box>
                                            <Text>Team</Text>
                                        </Box>
                                        {rounds.map((r) => (
                                            <Box key={r}>
                                                <Text>{r}</Text>
                                            </Box>
                                        ))}
                                        <Box>
                                            <Text>Total</Text>
                                        </Box>
                                    </Grid>
                                    {participantRoundStats.map((participant) => (
                                        <Grid key={participant.id} columns={["1fr", "5fr", ...rounds.map((r) => "2fr"), "2fr", "1fr"]} align="center" gap="medium">
                                            <Text size="large" weight="bold">
                                                {participantRoundStats.indexOf(participant) + 1}
                                            </Text>
                                            <Box direction="row" justify="start" align="center" gap="small">
                                                <img src={participant.team.logoUri} width="60px" alt="" />
                                                <TeamName team={participant.team} size="large" color={participant.team.color}></TeamName>
                                            </Box>
                                            {rounds.map((round, i) => (
                                                <Box key={round}>
                                                    <SeedMatchParticipantStats add={false} participant={participant} seedMatch={seedMatch} disabled={isLoading} round={i + 1} handleChange={loadSeedMatch}></SeedMatchParticipantStats>
                                                </Box>
                                            ))}
                                            <Box>
                                                {participant["Total"] &&
                                                    values(participant["Total"]).map(({ stat, value }) => (
                                                        <Text key={participant.id + "Total" + stat.id}>
                                                            {StatIcon(stat)} {StatValue(stat, value)}
                                                        </Text>
                                                    ))}
                                            </Box>
                                            <SeedMatchParticipantStats add={true} participant={participant} seedMatch={seedMatch} disabled={isLoading} handleChange={loadSeedMatch}></SeedMatchParticipantStats>
                                        </Grid>
                                    ))}
                                    {/* 
                                    {statRoundColumns.map((round, index) => (
                                        <Text key={"c" + index}>Round {index + 1}</Text>
                                    ))} */}
                                    {/* {participants.map((participant, index) => (
                                        <Box key={"s" + index} gap="small">
                                            <TeamRow team={participant.team}>
                                                <Grid columns={statRoundColumns}>
                                                    {participant.stats.map(({ stat, value }, index) => (
                                                        <Text key={index}>
                                                            {StatIcon(stat)} {StatValue(stat, value)}
                                                        </Text>
                                                    ))}
                                                </Grid>
                                            </TeamRow>
                                        </Box>
                                    ))} */}
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path={`teams`}
                        element={
                            <>
                                <Box pad={"small"} gap="small">
                                    {participants.map((participant, index) => (
                                        <TeamRow team={participant.team} key={index}>
                                            <Box direction="row" gap="small">
                                                <Button color="status-error" onClick={() => startRemoveParticipant(participant.team)} label="Remove Participant"></Button>
                                            </Box>
                                        </TeamRow>
                                    ))}
                                </Box>
                                {hasWritePermission && <Button icon={plus} label="Add Participant" onClick={() => setShowAddParticipantModal(true)}></Button>}
                                <StyledModal isOpen={showAddParticipantModal} setIsOpen={setShowAddParticipantModal}>
                                    <Box width={"medium"}>
                                        <FormField label="Search">
                                            <TextInput size="xs" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} />
                                        </FormField>
                                        <List
                                            pad="xs"
                                            data={teamSearchResult}
                                            itemKey="id"
                                            primaryKey={(team: TeamViewModel) => (
                                                <Text size="small" key={team.id}>
                                                    <Avatar name={team.name} src={team.logoUri} round size="25px"></Avatar> {team.name}
                                                </Text>
                                            )}
                                            action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => addParticipantToSeedMatch(item)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                                        ></List>
                                        <Text>
                                            <pre style={{ whiteSpace: "pre-wrap" }}>{participantCreateError}</pre>
                                        </Text>
                                    </Box>
                                </StyledModal>

                                <StyledModal isOpen={showRemoveParticipantModal} setIsOpen={setShowRemoveParticipantModal}>
                                    <Box width={"medium"}>
                                        <Text>Are you sure you want to remove this participant?</Text>
                                        <Text>All stats will also be removed for this participant.</Text>
                                        <Text>
                                            <pre style={{ whiteSpace: "pre-wrap" }}>{participantRemoveError}</pre>
                                        </Text>
                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowRemoveParticipantModal(false)} label="Cancel"></Button>
                                            <Button primary onClick={() => removeParticipantFromSeedMatch()} label="Remove" color="status-critical"></Button>
                                        </Box>
                                    </Box>
                                </StyledModal>
                            </>
                        }
                    />
                    <Route
                        path={`details`}
                        element={
                            <>
                                <SeedMatchDetails seedMatch={seedMatch} isBusy={!hasWritePermission || isUpdating} onChange={onChange}></SeedMatchDetails>
                                <br />
                                <Box direction="row" gap="small">
                                    {hasWritePermission && (
                                        <>
                                            {seedMatch.archived && <Button onClick={() => startArchive()} label="Unarchive" color="status-ok"></Button>}
                                            {!seedMatch.archived && <Button onClick={() => startArchive()} label="Archive" color="status-warning"></Button>}
                                        </>
                                    )}

                                    {hasWritePermission && <Button onClick={startRemoveAllStats} disabled={isUpdating} color="status-error" label="Remove All Stats"></Button>}
                                    {hasWritePermission && <Button primary onClick={update} disabled={isUpdating} label="Update"></Button>}
                                </Box>

                                <StyledModal isOpen={showArchiveModal} setIsOpen={setShowArchiveModal}>
                                    <Box width={"medium"}>
                                        {seedMatch.archived && <Text>Are you sure you want to unarchive this seed match?</Text>}
                                        {!seedMatch.archived && <Text>Are you sure you want to archive this seed match?</Text>}
                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowArchiveModal(false)} label="Cancel"></Button>
                                            {seedMatch.archived && <Button primary onClick={() => archive()} label="Unarchive" color="status-ok"></Button>}
                                            {!seedMatch.archived && <Button primary onClick={() => archive()} label="Archive" color="status-warning"></Button>}
                                        </Box>
                                    </Box>
                                </StyledModal>

                                <StyledModal isOpen={showRemoveAllStatsModal} setIsOpen={setShowRemoveAllStatsModal}>
                                    <Box width={"medium"}>
                                        <Text>Are you sure you want to remove all stats for this seed match?</Text>

                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowRemoveAllStatsModal(false)} label="Cancel"></Button>
                                            <Button primary onClick={() => removeAllStats()} label="Remove Stats" color="status-error"></Button>
                                        </Box>
                                    </Box>
                                </StyledModal>
                            </>
                        }
                    />
                    <Route
                        path="public"
                        element={
                            <Box gap="small">
                                <Button href={"/public/seedMatches/" + seedMatch.id} icon={idBadge} label="View Public Page"></Button>

                                <Box>
                                    <FormField label="Dark Mode">
                                        <CheckBox checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Use Team Color">
                                        <CheckBox checked={useTeamColor} onChange={(e) => setUseTeamColor(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Reverse">
                                        <CheckBox checked={reverse} onChange={(e) => setReverse(e.target.checked)} />
                                    </FormField>

                                    {/* <FormField label="Reverse">
                                        <ColorPicker style={{ backgroundColor: backgroundColor }}>
                                            <input type="color" value={color} onChange={(e) => selectColor(e.target.value)} />
                                        </ColorPicker>
                                    </FormField> */}
                                </Box>
                                <Button onClick={copyLink("/public/seedMatches/" + seedMatch.id)} icon={chain} label="Copy Table Link"></Button>
                                <iframe
                                    style={{
                                        border: "5px solid #999",
                                        borderRadius: "5px",
                                    }}
                                    src={"/public/seedMatches/" + seedMatch.id}
                                    width="100%"
                                    height="700px"
                                ></iframe>
                            </Box>
                        }
                    />
                    <Route
                        path={`errors`}
                        element={
                            <>
                                {" "}
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </>
                        }
                    />
                </Routes>
            </SkeletonWrapper>
        </div>
    );
};

export default SeedMatchProfile;
